import { useState, useEffect } from 'react';
import { Table, Button, Tag, Input, Select, DatePicker, Pagination, Cascader, Popconfirm, message, Modal } from 'antd';
import type { TableProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/cascader';
import { PlusSquareOutlined, BulbOutlined, DashboardOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { DeviceItem } from '@/interface/device';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Https from '@/https';
import './Device.css'
const { RangePicker } = DatePicker;
type TableRowSelection<T extends object = object> = TableProps<T>['rowSelection'];
export default function Device() {
  const [messageApi, messageContextHolder] = message.useMessage();
  const [deviceList, setDevieceList] = useState([]);
  const [deviceCanRefreshList, setDeviceCanRefreshList] = useState([]);
  const [toggleReset, setToggleReset] = useState(false);
  const [deviceColumns] = useState([
    {
      title: '设备唯一码',
      dataIndex: 'sn',
      key: 'sn',
      width: 180
    },
    {
      title: '第三方唯一码',
      dataIndex: 'number',
      key: 'number',
      width: 180
    },
    {
      title: '设备名称',
      dataIndex: 'name',
      key: 'name',
      width: 200
    },
    {
      title: '所属公司',
      dataIndex: 'tenantName',
      key: 'tenantName',
    },
    {
      title: '设备类型',
      dataIndex: 'typeName',
      key: 'typeName',
    },
    {
      title: '设备厂商',
      dataIndex: 'deviceFactoryName',
      key: 'deviceFactoryName',
    },
    {
      title: '设备型号',
      dataIndex: 'modelName',
      key: 'modelName',
    },
    {
      title: '在线状态',
      dataIndex: 'isOnlineText',
      key: 'isOnlineText',
      render: (isOnlineText: string, record: any) => {
        return record.isOnline ? <Tag color="success">{isOnlineText}</Tag>: <Tag color="error">{isOnlineText}</Tag>;
      }
    },
    {
      title: '设备状态',
      dataIndex: 'statusName',
      key: 'statusName',
    },
    {
      title: '服务开始时间',
      dataIndex: 'servicesStartTime',
      key: 'servicesStartTime',
      width: 180
    },
    {
      title: '服务结束时间',
      dataIndex: 'servicesEndTime',
      key: 'servicesEndTime',
      width: 180
    },
    {
      title: '操作',
      key: 'action',
      dataIndex: 'action',
      width: 180,
      fixed: 'right' as const,
      render: (text: any, record: any) => {
        return (
          <>
            <Button color="primary" variant="link" size="small" onClick={() => handleDeviceView(record)}>详情</Button>
            <Button color="cyan" variant="link" size="small" onClick={() => handleDeviceEdit(record)}>编辑</Button>
            <Popconfirm
              title="提示"
              description="确定删除运营区域?"
              onConfirm={() => handleDeviceRemove(record)}
              okText="确定"
              cancelText="取消">
              <Button color="danger" variant="link" size="small">回收</Button>
            </Popconfirm>
          </>
        )
      }
    }
  ]);
  let [pageIndex, setPageIndex] = useState(1);
  let [pageSize, setPageSize] = useState(10);
  let [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleDeviceCreate = () => {
    navigate(`/main/device/create`);
    dispatch({
      type: 'PUSH_MENU_KEYPATH',
      payload: {
        selectedKeys: 'deviceCreate',
        keyPath: '添加设备',
      }
    })
  }

  const handleDeviceView = (record: DeviceItem) => {
    navigate(`/main/device/detail`, { state: { deviceId: record.deviceId } });
    dispatch({
      type: 'PUSH_MENU_KEYPATH',
      payload: {
        selectedKeys: 'deviceDetail',
        keyPath: '设备详情',
      }
    })
  }

  const handleDeviceEdit = (record: DeviceItem) => {
    navigate(`/main/device/edit`, { state: { deviceId: record.deviceId } });
    dispatch({
      type: 'PUSH_MENU_KEYPATH',
      payload: {
        selectedKeys: 'deviceEdit',
        keyPath: '编辑设备',
      }
    })
  }

  const handleDeviceRemove = (record: DeviceItem) => {
    Https.post({
      url: 'Api/device/V1/device/DeviceRecy',
      data: {
        id: record.deviceId
      }
    }).then(res => {
      if(res.state === 1) {
        messageApi.success('操作成功');
        getDeviceList();
      } else {
        messageApi.error(res.data.errorMessage || res.errorMessage || '操作失败');
      }
    })
  }

  const [sn, setSn] = useState('');
  const onSnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSn(e.target.value);
  }

  const [name, setName] = useState('');
  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }

  const [tenantId, setTenantId] = useState<string|undefined>(undefined);
  const [tenantList, setTenantList] = useState([]);
  const tenantChange = (value: string) => {
    setTenantId(value);
  }

  const getTenantList = () => {
    Https.post({
      url: 'Api/Tenant/V1/tenant/list',
      data: {}
    }).then(res => {
      if(res.state === 1) {
        setTenantList(res.data.tenantList.map((item: any) => {
          return {
            value: item.id,
            label: item.name
          }
        }));
      }
    })
  }

  const [status, setStatus] = useState<string | undefined>(undefined);
  const deviceStatusList = [
    { value: '1', label: '使用中' },
    { value: '2', label: '暂停使用' },
    { value: '3', label: '预投放' },
  ];

  const deviceStatusChange = (value: string) => {
    setStatus(value);
  }

  const [deviceType, setDeviceType] = useState<string[]>([]);
  const [deviceTypeOptions, setDeviceTypeOptions] = useState<DefaultOptionType[]>([]);
  const getDeviceTypeOptions = () => {
    Https.post({
      url: 'Api/device/V1/deviceEnum/FindDeviceTypeEnumList',
      data: {}
    }).then((res) => {
      if(res.state === 1) {
        let list = res.data;
        getModelOptions(list);
      }
    })
  }

  const getModelOptions = (deviceType: DefaultOptionType[]) => {
    Https.post({
      url: 'Api/device/V1/DeviceModel/List/By',
      data: {}
    }).then((res) => {
      if(res.state === 1) {
        let deviceTypeList = deviceType.map(item => {
          return {
            value: item.value,
            label: item.text,
            children: []
          }
        })
        let list = res.data.deviceModels;
        for(let item of list) {
          for(let type of deviceTypeList) {
            if(type.value === item.deviceType && item.isActive) {
              (type.children as any[]).push({
                value: item.modelId,
                label: item.name
              });
            }
          }
        }
        setDeviceTypeOptions(deviceTypeList);
      }
    })
  }
  const deviceTypeChange = (value: string[]) => {
    setDeviceType(value);
  }

  const [createTime, setCreateTime] = useState<[start: Dayjs | null | undefined, end: Dayjs | null | undefined]>([undefined, undefined]);
  const onCreateTimeChange = (value: any) => {
    if(value?.length === 2) {
      setCreateTime([dayjs(value[0]), dayjs(value[1])]);
    } else {
      setCreateTime([undefined, undefined]);
    }
  }

  const [modifyTime, setModifyTime] = useState<[start: Dayjs | null | undefined, end: Dayjs | null | undefined]>([undefined, undefined]);
  const onModifyTimeChange = (value: any) => {
    if(value?.length === 2) {
      setModifyTime([dayjs(value[0]), dayjs(value[1])]);
    } else {
      setModifyTime([undefined, undefined]);
    }
  }

  const [loading, setLoading] = useState(false);
  const reset = () => {
    setSn('');
    setName('');
    setTenantId(undefined);
    setStatus(undefined);
    setDeviceType([]);
    setCreateTime([undefined, undefined]);
    setModifyTime([undefined, undefined]);
    setPageIndex(1);
    setToggleReset(true);
  }

  const search = () => {
    setPageIndex(1);
    getDeviceList();
  }

  const getDeviceList = () => {
    setLoading(true);
    let params: {
      pageIndex: number;
      pageSize: number;
      sn: string;
      name: string;
      status: string | undefined;
      deviceType?: string;
      tenantId: string | undefined;
      modelId?: string;
    } = {
      pageIndex,
      pageSize,
      sn,
      name,
      tenantId,
      status,
    }
    if(deviceType.length > 0) {
      params.deviceType = deviceType[0];
    }
    if(deviceType.length === 2) {
      params.modelId = deviceType[1];
    }
    Https.post({
      url: 'Api/device/V1/device/DeviceList',
      data: {
        ...params,
      }
    }).then(res => {
      if(res.state) {
        setTotal(res.data.totalCount);
        setDevieceList(res.data.deviceList);
        setDeviceCanRefreshList(res.data.deviceList.filter((item: any) => item.tenantId !== '0'))
      }
    }).finally(() => {
      setLoading(false);
    })
  }

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: record => ({
      disabled: record.tenantId !== '0',
    }),
  };

  const [selectedRefreshRowKeys, setSelectedRefreshRowKeys] = useState<React.Key[]>([]);
  const onSelectRefreshChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRefreshRowKeys(newSelectedRowKeys);
  };
  const rowCanRefreshSelection: TableRowSelection<any> = {
    selectedRowKeys: selectedRefreshRowKeys,
    onChange: onSelectRefreshChange,
  };

  const [allocateDeviceVisible, setAllocateDeviceVisible] = useState<boolean>(false);
  const [allocateTenantId, setAllocateTenantId] = useState<string | undefined>(undefined);
  const [allocateDate, setAllocateDate] = useState<[Dayjs | null | undefined, Dayjs | null | undefined]>([undefined, undefined]);
  const handleAllocateDeviceCancel = () => {
    setAllocateDeviceVisible(false);
    setAllocateTenantId(undefined);
    setAllocateDate([undefined, undefined]);
  }

  const handleAllocateDeviceConfirm =() => {
    if(!allocateTenantId) {
      message.warning('请选择所属公司');
      return;
    }
    if(!allocateDate[0] || !allocateDate[1]) {
      message.warning('请选择分配时间');
      return;
    }
    Https.post({
      url: 'Api/device/V1/device/AllotDevice',
      data: {
        tenantId: allocateTenantId,
        date: [dayjs(allocateDate[0]).format('YYYY-MM-DD'), dayjs(allocateDate[1]).format('YYYY-MM-DD')],
        deviceIdList: selectedRowKeys,
      }
    }).then(res => {
      if(res.state === 1) {
        handleAllocateDeviceCancel();
        message.success('分配成功');
        setSelectedRowKeys([]);
        getDeviceList();
      }
    })
  }

  const [serviceDateVisible, setServiceDateVisible] = useState<boolean>(false);
  const [refreshDate, setRefreshDate] = useState<string | undefined>(undefined);

  const deviceCanRefreshColumns = [
    {
      title: '设备唯一码',
      dataIndex: 'sn',
      key: 'sn',
      width: 180
    },
    {
      title: '第三方唯一码',
      dataIndex: 'number',
      key: 'number',
      width: 180
    },
    {
      title: '设备名称',
      dataIndex: 'name',
      key: 'name',
      width: 200
    },
    {
      title: '所属公司',
      dataIndex: 'tenantName',
      key: 'tenantName',
    },
    {
      title: '设备类型',
      dataIndex: 'typeName',
      key: 'typeName',
    },
    {
      title: '设备型号',
      dataIndex: 'modelName',
      key: 'modelName',
    },
    {
      title: '在线状态',
      dataIndex: 'isOnlineText',
      key: 'isOnlineText',
      render: (isOnlineText: string, record: any) => {
        return record.isOnline ? <Tag color="success">{isOnlineText}</Tag>: <Tag color="error">{isOnlineText}</Tag>;
      }
    },
    {
      title: '设备状态',
      dataIndex: 'statusName',
      key: 'statusName',
    },
    {
      title: '服务开始时间',
      dataIndex: 'servicesStartTime',
      key: 'servicesStartTime',
      width: 180
    },
    {
      title: '服务结束时间',
      dataIndex: 'servicesEndTime',
      key: 'servicesEndTime',
      width: 180
    },
  ];

  const handleServiceDateConfirm = () => {
    if(!refreshDate) {
      message.warning('请选择服务时间');
      return;
    }
    if(!selectedRefreshRowKeys.length) {
      message.warning('请选择设备');
      return;
    }
    Https.post({
      url: 'Api/device/V1/Device/UpdateDeviceServiceUseDate',
      data: {
        serviceEndTime: dayjs(refreshDate).format('YYYY-MM-DD'),
        deviceIds: selectedRefreshRowKeys,
      }
    }).then(res => {
      if(res.state === 1) {
        message.success('服务时间更新成功');
        handleServiceDateCancel();
      }
    })
  }

  const handleServiceDateCancel = () => {
    setServiceDateVisible(false);
    setRefreshDate(undefined);
    setSelectedRefreshRowKeys([]);
  }

  useEffect(() => {
    getTenantList();
    getDeviceTypeOptions();
    getDeviceList();
  }, [])

  useEffect(() => {
    if(toggleReset) {
      getDeviceList();
      setToggleReset(false);
    }
  }, [toggleReset])

  useEffect(() => {
    getDeviceList();
  }, [pageIndex, pageSize])

  return (
    <section className="Device Page-Container">
      <section className="search-box">
        <section className="search-item">
          <span className="search-label">设备唯一码</span>
          <Input placeholder="请输入设备ID" value={sn} allowClear onChange={onSnChange} />
        </section>
        <section className="search-item">
          <span className="search-label">设备名称</span>
          <Input placeholder="请输入设备名称" value={name} allowClear onChange={onNameChange} />
        </section>
        <section className="search-item">
          <span className="search-label">所属公司</span>
          <Select
            allowClear
            placeholder="请选择所属公司"
            value={tenantId}
            style={{ width: 200 }}
            options={tenantList}
            onChange={tenantChange}
          />
        </section>
        <section className="search-item">
          <span className="search-label">设备状态</span>
          <Select
            allowClear
            placeholder="请选择设备状态"
            value={status}
            style={{ width: 200 }}
            options={deviceStatusList}
            onChange={deviceStatusChange}
          />
        </section>
        <section className="search-item">
          <span className="search-label">设备型号</span>
          <Cascader
            allowClear
            value={deviceType}
            options={deviceTypeOptions}
            onChange={(value) => deviceTypeChange(value as string[])}
            style={{ width: 200 }}
            placeholder="请选择设备型号"
          />
        </section>
        {/* <section className="search-item width-search-item">
          <span className="search-label">安装时间</span>
          <RangePicker
            showTime
            allowClear
            value={createTime}
            format="YYYY-MM-DD HH:mm:ss"
            onChange={onCreateTimeChange}
          />
        </section>
        <section className="search-item width-search-item">
          <span className="search-label">更新时间</span>
          <RangePicker
            showTime
            allowClear
            value={modifyTime}
            format="YYYY-MM-DD HH:mm:ss"
            onChange={onModifyTimeChange}
          />
        </section> */}
      </section>
      <section className="search-btns device-list-btns">
        <section>
          <Button type="primary" onClick={() => setAllocateDeviceVisible(true)} disabled={selectedRowKeys.length === 0} icon={<BulbOutlined />} style={{marginLeft: '10px'}}>批量分配设备</Button>
          <Button type="primary" onClick={() => setServiceDateVisible(true)} disabled={deviceCanRefreshList.length === 0}  icon={<DashboardOutlined />} style={{marginLeft: '10px'}}>批量更新服务到期时间</Button>
        </section>
        <section>
          <Button onClick={reset}>重置</Button>
          <Button type="primary" onClick={search}>查询</Button>
          <Button type="primary" onClick={handleDeviceCreate} icon={<PlusSquareOutlined />} style={{marginLeft: '10px'}}>添加设备</Button>
        </section>
      </section>
      <section>
        <Table
          dataSource={deviceList}
          rowKey="deviceId"
          columns={deviceColumns}
          pagination={false}
          scroll={{x: 'max-content', y: 'calc(100vh - 410px)'}}
          loading={loading}
          rowSelection={rowSelection}
         />
      </section>
      <section className="page-box">
        <Pagination
          align="end"
          total={total}
          showTotal={(total) => `共 ${total} 条`}
          current={pageIndex}
          defaultPageSize={10}
          defaultCurrent={1}
          onChange={(page, pageSize) => {
            setPageIndex(page);
            setPageSize(pageSize);
          }}
        />
      </section>
      <Modal
        open={allocateDeviceVisible}
        title="批量分配设备"
        onOk={handleAllocateDeviceConfirm}
        onCancel={handleAllocateDeviceCancel}
        destroyOnHidden={true}>
        <section className="modal-form-item">
          <span className="label required">所属公司</span>
          <Select
            allowClear
            placeholder="请选择所属公司"
            value={allocateTenantId}
            style={{ width: 200 }}
            options={tenantList}
            onChange={(value) => setAllocateTenantId(value as string)}
          />
        </section>
        <section className="modal-form-item">
          <span className="label required">服务时间</span>
          <RangePicker
            allowClear
            value={allocateDate}
            format="YYYY-MM-DD"
            onChange={(date) => {
              if(date?.length === 2) {
                setAllocateDate([date[0], date[1]]);
              } else {
                setAllocateDate([undefined, undefined]);
              }
            }}
          />
        </section>
      </Modal>
      <Modal
        open={serviceDateVisible}
        title="批量更新服务到期时间"
        width="80%"
        onOk={handleServiceDateConfirm}
        onCancel={handleServiceDateCancel}
        destroyOnHidden={true}>
        <section className="modal-form-item">
          <span className="label required">服务到期时间</span>
          <DatePicker
            allowClear
            value={refreshDate}
            format="YYYY-MM-DD"
            onChange={(date) => {
              if(date) {
                setRefreshDate(date)
              } else {
                setRefreshDate(undefined);
              }
            }}
          />
        </section>
        <section>
          <Table
            dataSource={deviceCanRefreshList}
            rowKey="deviceId"
            columns={deviceCanRefreshColumns}
            pagination={false}
            scroll={{x: 'max-content', y: 400}}
            loading={loading}
            rowSelection={rowCanRefreshSelection}
          />
        </section>
      </Modal>
      { messageContextHolder }
    </section>
  )
}